import { DataTable } from "@/components/data-table/data-table.tsx";
import { Button } from "@/components/ui/button.tsx";
import { AssignMentorModal } from "@/components/users/user-details/supervisor-mentors/assign-mentor-modal";
import { getColumns } from "@/components/users/user-details/supervisor-mentors/columns";
import { SupervisorMentor } from "@/lib/api/user.ts";
import { useDataTable } from "@/lib/hooks/use-data-table.ts";
import { useRouter } from "@tanstack/react-router";
import { PlusIcon, Trash2Icon } from "lucide-react";
import { useState } from "react";
import { BatchOperationModal } from "./batch-operation-modal.tsx";

interface SupervisorMentorsProps {
  userId: string;
  mentors: SupervisorMentor[];
  isPending: boolean;
  canAssignSupervisor: boolean;
  canUnassignSupervisor: boolean;
}

export const SupervisorMentors = ({
  userId,
  isPending,
  mentors,
  canAssignSupervisor,
  canUnassignSupervisor,
}: SupervisorMentorsProps) => {
  const router = useRouter();
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [isBatchModalOpen, setIsBatchModalOpen] = useState(false);
  const [selectedMentors, setSelectedMentors] = useState<SupervisorMentor[]>(
    [],
  );

  const { table } = useDataTable({
    columns: getColumns(userId, canUnassignSupervisor, mentors.length),
    data: mentors || [],
    defaultPerPage: 10,
    pageCount: -1,
    filterFields: [],
    disablePagination: true,
    enableRowSelection: canUnassignSupervisor,
  });

  const selectedRows = table.getSelectedRowModel().rows;
  const hasSelection = canUnassignSupervisor && selectedRows.length > 0;

  const handleBatchModalOpen = () => {
    setIsBatchModalOpen(true);
    setSelectedMentors(selectedRows.map((row) => row.original));
    table.toggleAllRowsSelected(false); // Clear all row selections
  };

  const handleBatchModalClose = () => {
    setIsBatchModalOpen(false);
  };

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <div className="flex gap-2">
          {hasSelection && (
            <Button variant="destructive" onClick={handleBatchModalOpen}>
              <Trash2Icon className="mr-2 h-4 w-4" />
              Unassign Selected
            </Button>
          )}
        </div>
        {canAssignSupervisor && (
          <Button onClick={() => setIsAssignModalOpen(true)}>
            <PlusIcon className="mr-2 h-4 w-4" />
            Assign New Mentor
          </Button>
        )}
      </div>
      <DataTable
        table={table}
        isPending={isPending}
        disablePagination
        onRowClick={(row, options) => {
          const url = `/users/${row.mentor_id}`;
          if (options?.getNavigationUrl) {
            return url;
          }
          router.navigate({ to: url });
        }}
      />
      {canAssignSupervisor && (
        <AssignMentorModal
          isOpen={isAssignModalOpen}
          onClose={() => setIsAssignModalOpen(false)}
          userId={userId}
          assignedMentors={mentors.map((mentor) => ({
            id: mentor.mentor_id,
            name: mentor.mentor_name,
          }))}
        />
      )}
      {canUnassignSupervisor && (
        <BatchOperationModal
          isOpen={isBatchModalOpen}
          onClose={handleBatchModalClose}
          operation="unassign"
          selectedMentors={selectedMentors}
          userId={userId}
        />
      )}
    </div>
  );
};
